import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { User as UserIcon, MoreHorizontal } from 'lucide-react';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';

const searchSchema = z.object({
  q: z.string().optional(),
  page: z.number().int().nonnegative().optional(),
});

export const Route = createFileRoute('/_authed/users/')({
  validateSearch: (input): { q?: string; page?: number } => {
    const r = searchSchema.safeParse(input);
    return r.success ? r.data : {};
  },
  component: UsersListPage,
});

interface UserDto {
  id: string;
  email: string;
  displayName?: string;
  role: 'user' | 'admin' | 'superadmin';
  plan: 'FREE' | 'SILVER' | 'GOLD' | 'PLATINUM';
  createdAt: number;
  lastLogin?: number;
  isSuspended?: boolean;
}

const mock: UserDto[] = Array.from({ length: 22 }, (_, i) => ({
  id: `u_${i}`,
  email: `usuario${i}@empresa.com`,
  displayName: `Usuario ${i}`,
  role: (['user', 'admin', 'superadmin'] as const)[i % 3],
  plan: (['FREE', 'SILVER', 'GOLD', 'PLATINUM'] as const)[i % 4],
  createdAt: Date.now() - i * 86400_000 * 5,
  lastLogin: Date.now() - i * 3600_000 * 4,
  isSuspended: i % 11 === 0,
}));

function UsersListPage() {
  const search = Route.useSearch();
  const { data, isLoading } = useQuery({
    queryKey: ['users', search],
    queryFn: async (): Promise<{ items: UserDto[]; total: number }> => {
      try {
        return await api.get<{ items: UserDto[]; total: number }>(
          '/admin/users',
          { query: { q: search.q, page: search.page ?? 0 } },
        );
      } catch {
        return { items: mock, total: mock.length };
      }
    },
  });

  const columns: ColumnDef<UserDto>[] = React.useMemo(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Nombre',
        cell: ({ row }) => (
          <Link
            to="/users/$userId"
            params={{ userId: row.original.id }}
            className="flex items-center gap-2 hover:underline"
          >
            <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">
              <UserIcon className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium">{row.original.displayName}</div>
              <div className="text-xs text-muted-foreground">
                {row.original.email}
              </div>
            </div>
          </Link>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Rol',
        cell: ({ row }) => <Badge variant="outline">{row.original.role}</Badge>,
      },
      {
        accessorKey: 'plan',
        header: 'Plan',
        cell: ({ row }) => <Badge variant="secondary">{row.original.plan}</Badge>,
      },
      {
        accessorKey: 'lastLogin',
        header: 'Último acceso',
        cell: ({ row }) =>
          row.original.lastLogin ? (
            <span className="text-muted-foreground">
              {formatRelative(row.original.lastLogin)}
            </span>
          ) : (
            <span className="text-muted-foreground">Nunca</span>
          ),
      },
      {
        accessorKey: 'isSuspended',
        header: 'Estado',
        cell: ({ row }) =>
          row.original.isSuspended ? (
            <Badge variant="destructive">Suspendido</Badge>
          ) : (
            <Badge variant="success">Activo</Badge>
          ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Acciones">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/users/$userId" params={{ userId: row.original.id }}>
                  Ver perfil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>Suspender</DropdownMenuItem>
              <DropdownMenuItem>Resetear contraseña</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Usuarios</h1>
        <p className="text-muted-foreground">
          {data?.total ?? mock.length} usuarios registrados
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Listado de usuarios</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data?.items ?? mock}
            searchKey="displayName"
            searchPlaceholder="Buscar usuarios…"
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
